import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Input,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableColumnsType } from 'antd';

import AddNewsModal from './AddNewsModal';
import DisplayNameEditor from './DisplayNameEditor';
import {
  BlueprintNewsFeed,
  BlueprintNewsItem,
  changeBlueprintNews,
  fetchBlueprintNews,
  saveBlueprintNewsName,
} from './presentationApi';

type Props = { blueprintId: number; variantId: number; readOnly?: boolean };

const BlueprintNewsView: React.FC<Props> = ({
  blueprintId,
  variantId,
  readOnly,
}) => {
  const [feed, setFeed] = useState<BlueprintNewsFeed>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [revision, setRevision] = useState(0);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setFeed(undefined);
    fetchBlueprintNews(blueprintId, variantId)
      .then(result => {
        if (active) setFeed(result);
      })
      .catch(reason => {
        if (active) setError(reason.message || 'Could not load news.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [blueprintId, variantId, revision]);

  const locked = readOnly || feed?.is_locked;
  const membership = feed?.membership;
  const canChange = !readOnly && Boolean(membership?.can_change);
  const remove = async (item: BlueprintNewsItem) => {
    setRemoving(item.id);
    setError('');
    try {
      setFeed(
        await changeBlueprintNews(blueprintId, variantId, {
          remove: [item.id],
        }),
      );
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'Could not remove news.',
      );
    } finally {
      setRemoving(null);
    }
  };
  const query = search.trim().toLowerCase();
  const rows = (feed?.items || []).filter(item =>
    `${item.id} ${item.headline} ${item.rephrased_headline || ''}`
      .toLowerCase()
      .includes(query),
  );
  const columns: TableColumnsType<BlueprintNewsItem> = [
    {
      title: 'Date (UTC)',
      key: 'date',
      width: 155,
      render: (_value, item) => (
        <Space orientation="vertical" size={0}>
          <span>
            {new Date(item.timestamp)
              .toISOString()
              .slice(0, 16)
              .replace('T', ' ')}
          </span>
          <Typography.Text type="secondary">News #{item.id}</Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Headline',
      dataIndex: 'headline',
      width: '35%',
      render: (value, item) => (
        <Space orientation="vertical" size={2}>
          <span style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
            {item.source === 'ai_curated' && (
              <Tag color="purple">AI Curated</Tag>
            )}
            {value}
          </span>
          {item.source === 'ai_curated' && item.original_headline && (
            <Typography.Text type="secondary">
              Original: {item.original_headline}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Display name (player headline)',
      key: 'display_name',
      render: (_value, item) => (
        <DisplayNameEditor
          key={`${item.id}:${revision}`}
          label={`news #${item.id} display name`}
          value={item.rephrased_headline}
          fallback={item.headline}
          disabled={locked}
          onSave={async name => {
            const saved = await saveBlueprintNewsName(
              blueprintId,
              variantId,
              item,
              name,
            );
            setFeed(
              current =>
                current && {
                  ...current,
                  items: current.items.map(row =>
                    row.id === saved.id ? saved : row,
                  ),
                },
            );
          }}
        />
      ),
    },
    ...(canChange
      ? [
          {
            title: '',
            key: 'remove',
            width: 110,
            render: (_value: unknown, item: BlueprintNewsItem) => (
              <Popconfirm
                title="Remove this story from the blueprint?"
                description="Trainees of this version will no longer receive it. The story itself is kept."
                okText="Remove"
                onConfirm={() => remove(item)}
              >
                <Button
                  size="small"
                  danger
                  loading={removing === item.id}
                  aria-label={`Remove news #${item.id} from the blueprint`}
                >
                  Remove
                </Button>
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        Blueprint news
      </Typography.Title>
      <Typography.Paragraph style={{ margin: 0 }}>
        Stories selected by this template’s dates and news filters. Display
        names are shared: saving one updates the headline everywhere this story
        is used. Leave it blank to use the original. Use Add news to bring in AI
        Curated stories: clean copies without dates, names or market moves.
      </Typography.Paragraph>
      {locked && (
        <Alert
          type="info"
          showIcon
          title="This version is locked. Create a new version to edit its news."
        />
      )}
      {!locked && membership && !membership.can_change && membership.reason && (
        <Alert type="info" showIcon title={membership.reason} />
      )}
      {canChange && membership?.separates_on_change && (
        <Alert
          type="info"
          showIcon
          title="This version shares its news with a launched room or another version. Your first change gives it a news list of its own; the room and the other versions keep theirs."
        />
      )}
      {error && <Alert type="error" showIcon title={error} />}
      <Space wrap>
        <Input.Search
          aria-label="Search blueprint news"
          placeholder="Search headlines or news ID"
          allowClear
          value={search}
          onChange={event => setSearch(event.target.value)}
          style={{ width: 320, maxWidth: '100%' }}
        />
        <Button
          loading={loading}
          onClick={() => setRevision(value => value + 1)}
        >
          Refresh news
        </Button>
        {canChange && (
          <Button type="primary" onClick={() => setAdding(true)}>
            Add news
          </Button>
        )}
        {feed && (
          <Typography.Text type="secondary">
            {feed.items.length} stories
            {canChange && membership && membership.variants_sharing > 1
              ? ` · changes apply to all ${membership.variants_sharing} difficulties of this version`
              : ''}
          </Typography.Text>
        )}
      </Space>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ defaultPageSize: 10, showSizeChanger: true }}
        scroll={{ x: 900 }}
        locale={{
          emptyText: error
            ? 'News could not be loaded.'
            : query
              ? 'No stories match your search.'
              : 'No news matches this template’s dates and filters.',
        }}
        expandable={{
          expandedRowRender: item => (
            <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>
              {item.body}
            </Typography.Paragraph>
          ),
          rowExpandable: item => Boolean(item.body),
        }}
      />
      {feed && adding && (
        <AddNewsModal
          open={adding}
          blueprintId={blueprintId}
          variantId={variantId}
          feed={feed}
          onClose={() => setAdding(false)}
          onAdded={setFeed}
        />
      )}
    </Space>
  );
};

export default BlueprintNewsView;
